import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {Router, RouterLink, RouterLinkActive} from '@angular/router';
import {MatTable, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatCell, MatCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatNoDataRow, MatTableDataSource} from '@angular/material/table';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {NewsAnnouncement} from './news-announcement';
import {PersonLogin, RoleName} from '../person/person-login';
import {DocumentService} from '../services/document-service';
import {IecTaskService} from '../services/iec-task-service';
import {NewsAnnouncementService} from '../services/news-announcement-service';
import {PersonLoginService} from '../services/person-login-service';
import {IecTask} from '../tasks/iec-task';
import {StringUtils} from '../utils/string-utils';
import {DialogService} from "../services/dialog.service";
import {FileUtils} from "../utils/file-utils";
import { DatePipe } from '@angular/common';
import {DocumentDownload} from "../model/document-download";
import {firstValueFrom} from "rxjs";

@Component({
    selector: 'main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, RouterLinkActive, DatePipe, MatTable, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatCell, MatCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatNoDataRow, MatSort, MatSortHeader]
})
export class MainComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly iecTaskService = inject(IecTaskService);
  private readonly newsAnnouncementService = inject(NewsAnnouncementService);
  private readonly router = inject(Router);
  private readonly documentService = inject(DocumentService);
  private readonly dialogService = inject(DialogService);


  // The table lives inside an @if (personLogin) block, so MatSort isn't
  // available until that view renders. A setter connects it to the data
  // source as soon as the directive appears.
  @ViewChild(MatSort) set sort(sort: MatSort) {
    if (sort) {
      this.taskDataSource.sort = sort;
    }
  }

  public personLogin!: PersonLogin;
  public taskDataSource: MatTableDataSource<IecTask> = new MatTableDataSource<IecTask>([]);
  public taskColumns: string[] = ['name', 'entityId', 'entityRevision', 'startTime'];
  public readonly newsAnnouncements = signal<NewsAnnouncement[]>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);
  public isInternalUser: boolean = false;
  readonly marketingImage1 = signal<string | ArrayBuffer>(undefined!);
  readonly marketingImage2 = signal<string | ArrayBuffer>(undefined!);

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.isInternalUser = this.personLoginService.isInternal();
      this.findMyWorkflowTasks();
      this.getActiveAnnouncements();
      this.loadMarketingImages();
    }
  }

  public findMyWorkflowTasks(): void {
    this.iecTaskService.findMyWorkflowTasks().subscribe(results => {
      this.taskDataSource.data = results || [];
    }, error => this.handleError(error));
  }

  public getActiveAnnouncements(): void {
    let brandIDs = this.personLoginService.getBrandIDs();
    this.newsAnnouncements.set([]);
    for (let brandID of brandIDs) {
      this.newsAnnouncementService.getActiveAnnouncements(brandID).subscribe(results => {
        this.newsAnnouncements.update(current => [...current, ...results]);
      }, error => this.handleError(error));
    }
  }

  public showPartsDialog(): void {
    this.dialogService.showPartsDialog(null!).subscribe({
      next: (result) => {
        if (result) {
          this.router.navigate(['quotes', result.id.entityId, result.id.entityRevision]);
        }
      }, error: (error) => this.handleError(error)
    });
  }

  public hasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  getLeadTimeUrl(): string {
    const brandName: string = this.personLoginService.getSelectedBrandName();
    switch (brandName) {
      case 'IEC':
        return 'https://info.iecokc.com/leadtimes';
      case 'CLM':
      default:
        return 'https://info.climatemaster.com/leadtimes';
    }
  }

  openUserPerformanceDefaultsDialog(): void {
    this.dialogService.showUserPerformanceDefaultsDialog();
  }

  loadMarketingImages(): void {
    const brandName: string = this.personLoginService.getSelectedBrandName();
    if (StringUtils.isNotBlank(brandName)) {
      this.getMarketingImageUrl(brandName, 'MarketingImage1.png').then((source: string) => this.marketingImage1.set(source));
      this.getMarketingImageUrl(brandName, 'MarketingImage2.png').then((source: string) => this.marketingImage2.set(source));
    }
  }

  private async getMarketingDocument(brandName: string, fileName: string): Promise<Blob> {
    const documentDownload: DocumentDownload = await firstValueFrom<DocumentDownload>(this.documentService.downloadSupportDocument(brandName, 'Marketing Documentation', fileName));
    if ((documentDownload?.fileBytes?.length || 0) > 0) {
      return FileUtils.convertToBlob(documentDownload);
    }
    return undefined!;
  }

  private async getMarketingImageUrl(brandName: string, fileName: string): Promise<string> {
    const fileBlob: Blob = await this.getMarketingDocument(brandName, fileName);
    if (fileBlob) {
      return URL.createObjectURL(fileBlob);
    }
    return '';
  }

  public downloadHolidaySchedule(): void {
    this.errorMessage.set(null!);
    const brandName: string = this.personLoginService.getSelectedBrandName();
    if (StringUtils.isNotBlank(brandName)) {
      const fileName: string = 'CLM-Holiday-Schedule.pdf';
      this.getMarketingDocument(brandName, fileName).then((fileBlob: Blob) => {
        if (fileBlob) {
          FileUtils.downloadFile(fileBlob, fileName);
        }
      });
    }
  }

  public showFindOrdersDialog(): void {
    this.dialogService.showPrmsOrdersSearchDialog();
  }

  public openStockUnitsDialog(): void {
    this.errorMessage.set(null!);
    this.dialogService.createStockQuoteFromDialog().subscribe({error: (error) => this.handleError(error)});
  }

}
